import React, { useState, useEffect, useCallback } from 'react'
import { fetchTemplates, createTemplateFolder, uploadTemplateFile, deleteTemplate } from '../lib/api'

function formatSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' Б'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' КБ'
  return (bytes / (1024 * 1024)).toFixed(1) + ' МБ'
}

function fileIcon(mime) {
  if (!mime) return '📄'
  if (mime.includes('pdf')) return '📕'
  if (mime.includes('image')) return '🖼️'
  if (mime.includes('word') || mime.includes('document')) return '📝'
  if (mime.includes('sheet') || mime.includes('excel')) return '📊'
  if (mime.includes('zip') || mime.includes('rar')) return '🗜️'
  return '📄'
}

export default function TemplatesPage({ isAdmin, uploaderName }) {
  const [folder, setFolder] = useState('')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [newFolderName, setNewFolderName] = useState('')
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try { setItems(await fetchTemplates(folder)) } catch (e) { alert('Ошибка загрузки: ' + e.message) }
    setLoading(false)
  }, [folder])

  useEffect(() => { load() }, [load])

  const breadcrumbs = folder ? folder.split('/') : []

  function navigateTo(idx) {
    setFolder(breadcrumbs.slice(0, idx + 1).join('/'))
  }

  async function handleCreateFolder() {
    if (!newFolderName.trim()) return
    setCreating(true)
    try {
      await createTemplateFolder(newFolderName.trim(), folder, uploaderName)
      setNewFolderName('')
      setShowNewFolder(false)
      await load()
    } catch (e) { alert('Ошибка: ' + e.message) }
    setCreating(false)
  }

  async function handleUpload(e) {
    const files = Array.from(e.target.files)
    if (!files.length) return
    setUploading(true)
    try {
      for (const file of files) await uploadTemplateFile(file, folder, uploaderName)
      await load()
    } catch (e) { alert('Ошибка загрузки: ' + e.message) }
    setUploading(false)
    e.target.value = ''
  }

  async function handleDelete(item) {
    if (!window.confirm(`Удалить "${item.name}"?`)) return
    try {
      await deleteTemplate(item.id, item.url)
      await load()
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

  const inp = { fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1px solid #e0e0dc', outline: 'none' }

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        <span onClick={() => setFolder('')} style={{ fontSize: 13, color: folder ? '#185FA5' : '#1a1a1a', cursor: folder ? 'pointer' : 'default', fontWeight: 600 }}>
          📁 Все документы
        </span>
        {breadcrumbs.map((crumb, idx) => (
          <React.Fragment key={idx}>
            <span style={{ color: '#ccc' }}>/</span>
            <span onClick={() => navigateTo(idx)}
              style={{ fontSize: 13, color: idx === breadcrumbs.length - 1 ? '#1a1a1a' : '#185FA5', cursor: idx === breadcrumbs.length - 1 ? 'default' : 'pointer', fontWeight: idx === breadcrumbs.length - 1 ? 600 : 400 }}>
              {crumb}
            </span>
          </React.Fragment>
        ))}
      </div>

      {/* Actions (admin only) */}
      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          {!showNewFolder ? (
            <button onClick={() => setShowNewFolder(true)}
              style={{ fontSize: 13, padding: '7px 14px', borderRadius: 8, border: '1px dashed #d0d0cc', background: 'transparent', cursor: 'pointer', color: '#555' }}>
              📁 + Новая папка
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') setShowNewFolder(false) }}
                placeholder="Название папки" style={{ ...inp, width: 180 }} />
              <button onClick={handleCreateFolder} disabled={creating}
                style={{ fontSize: 13, padding: '7px 12px', borderRadius: 8, border: 'none', background: '#1a1a1a', color: 'white', cursor: 'pointer' }}>
                {creating ? '...' : 'Создать'}
              </button>
              <button onClick={() => { setShowNewFolder(false); setNewFolderName('') }}
                style={{ fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1px solid #e0e0dc', background: 'white', cursor: 'pointer', color: '#888' }}>
                Отмена
              </button>
            </div>
          )}

          <label style={{ fontSize: 13, padding: '7px 14px', borderRadius: 8, border: 'none', background: uploading ? '#888' : '#1a1a1a', color: 'white', cursor: 'pointer', fontWeight: 500 }}>
            {uploading ? '⏳ Загрузка...' : '⬆️ Загрузить файл'}
            <input type="file" multiple onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
          </label>
        </div>
      )}

      {/* File list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#aaa', fontSize: 13 }}>Загрузка...</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#ccc', fontSize: 13 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📂</div>
          {isAdmin ? 'Папка пуста. Загрузите файлы или создайте папку.' : 'Документов пока нет.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {items.map(item => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'white', border: '1px solid #e8e8e5', borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 20, flexShrink: 0 }}>
                {item.is_folder ? '📁' : fileIcon(item.mime_type)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {item.is_folder ? (
                  <div onClick={() => setFolder(folder ? folder + '/' + item.name : item.name)}
                    style={{ fontWeight: 600, fontSize: 14, cursor: 'pointer', color: '#185FA5' }}>
                    {item.name}
                  </div>
                ) : (
                  <a href={item.url} target="_blank" rel="noreferrer"
                    style={{ fontWeight: 500, fontSize: 14, color: '#185FA5', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.name}
                  </a>
                )}
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                  {!item.is_folder && formatSize(item.size)}
                  {!item.is_folder && item.size && ' · '}
                  {item.uploaded_by_name && `Загрузил: ${item.uploaded_by_name}`}
                  {' · '}{new Date(item.created_at).toLocaleDateString('ru-RU')}
                </div>
              </div>
              {isAdmin && (
                <button onClick={() => handleDelete(item)}
                  style={{ fontSize: 13, padding: '4px 10px', borderRadius: 6, border: '1px solid #ffd0d0', background: '#fff5f5', color: '#E24B4A', cursor: 'pointer', flexShrink: 0 }}>
                  Удалить
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
